// ─── Qué está mirando la persona ─────────────────────────────────────────────
//
// El panel del asistente vive en el armazón, no en una página, así que no tiene
// `useParams`: deduce el contexto de la RUTA. Esta tabla es el único lugar donde
// se hace esa deducción.
//
// ES UNA PISTA, NO UNA AFIRMACIÓN. El servidor la valida leyendo el objeto con el
// permiso de quien pregunta, y si no resuelve, la conversación sigue como
// general. Por eso acá se puede errar sin consecuencias de seguridad: lo peor
// que pasa es que la respuesta no tenga los datos de la pantalla.
//
// AGREGAR UNA PANTALLA es agregar una entrada acá Y su resolución en el servidor
// (`resolveContext` en `assistant.functions.ts`). Sólo con la de acá, el servidor
// descarta la pista y nadie se enteraría de que el contexto no llegó.

export interface AssistantContextHint {
  kind: 'cap_round' | 'cap_program';
  id: string;
}

/** Deduce la pista desde el camino de la URL. `null` = conversación general,
 *  que NO es un caso degradado sino el más frecuente. */
export function assistantHintFromPath(pathname: string): AssistantContextHint | null {
  // /red/propuestas/:submissionId — el expediente de una ronda CAP, del lado de
  // LA RED: lo que un revisor tiene delante para decidir.
  const round = /^\/red\/propuestas\/([^/]+)/.exec(pathname);
  if (round?.[1]) return { kind: 'cap_round', id: decodeURIComponent(round[1]) };

  // /cap/:programId/... — el expediente del CENTRO: el CAP de UN PROGRAMA, que
  // es lo mismo mire la auditoría, la matriz, los PLOs o la propuesta. Son
  // cinco pantallas de un solo expediente, así que comparten conversación: no
  // tiene sentido que preguntar «¿cómo cumplo esto?» abra un hilo distinto
  // según desde qué pestaña se pregunte.
  //
  // `/cap/institucional` y `/cap/ficha/...` quedan FUERA a propósito: no hablan
  // de un programa, así que no son este expediente.
  const program = /^\/cap\/(?!institucional|ficha|curso)([^/]+)\//.exec(pathname);
  if (program?.[1]) return { kind: 'cap_program', id: decodeURIComponent(program[1]) };
  return null;
}

/** Cómo se nombra un contexto en el historial. Una lista de fechas sin decir de
 *  qué era cada conversación no sirve para volver a ninguna. */
export function assistantContextLabelKey(kind: string): string {
  if (kind === 'cap_round') return 'assistant.context.capRound';
  if (kind === 'cap_program') return 'assistant.context.capProgram';
  return 'assistant.context.tmaiProcess';
}

/** ¿La conversación habla del mismo objeto que la pantalla?
 *
 *  Compara TIPO e id. Hoy sólo hay una clase de expediente, así que el id
 *  bastaría — pero el día que haya dos, comparar sólo el id haría que dos
 *  objetos distintos con el mismo identificador se confundan en silencio. */
export function isSameContext(
  context: { kind: string; id: string },
  hint: AssistantContextHint,
): boolean {
  return context.kind === hint.kind && context.id === hint.id;
}
